import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, HelpCircle } from 'lucide-react';
import Layout from './Layout';
import PageHero from './PageHero';
import faqs from '../data/faqs';

const FAQPage = () => {
  return (
    <Layout breadcrumbItems={[
      { label: 'FAQ', href: '/faq/' }
    ]}>
      <Helmet defer={false}>
        <title>FAQ - Free Google SERP Checker Tool</title>
        <meta name="description" content="Frequently asked questions about our free Google SERP checker tool. Get answers about features, usage, accuracy, VPN alternatives, and more." />
        <meta name="keywords" content="serp checker faq, google serp checker questions, serp checker help, serp checker support, serp checker guide, serp checker frequently asked questions, free serp checker faq, serp checker tutorial questions" />
        <link rel="canonical" href="https://freeserpchecker.org/faq/" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://freeserpchecker.org/faq/" />
        <meta property="og:title" content="FAQ - Free Google SERP Checker Tool" />
        <meta property="og:description" content="Frequently asked questions about our free Google SERP checker tool. Get answers about features, usage, accuracy, VPN alternatives, and more." />
        <meta property="og:image" content="https://freeserpchecker.org/og-image.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://freeserpchecker.org/faq/" />
        <meta property="twitter:title" content="FAQ - Free Google SERP Checker Tool" />
        <meta property="twitter:description" content="Frequently asked questions about our free Google SERP checker tool. Get answers about features, usage, accuracy, VPN alternatives, and more." />
        <meta property="twitter:image" content="https://freeserpchecker.org/og-image.png" />

        {/* FAQ structured data — generated from the same `faqs` array that renders the
            visible answers, so it matches the page word for word (P6, A03). */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.question,
              acceptedAnswer: { '@type': 'Answer', text: f.answer },
            })),
          })}
        </script>
      </Helmet>

      <section id="faq" className="section-padding bg-white">
        <div className="container-max">
          <PageHero
            title="Frequently asked questions"
            subtitle="Answers about how the SERP checker works, what it can do, and why it needs no VPN."
          />

          {/* FAQ Grid */}
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {/* Native <details>/<summary>: every answer is always in the DOM (so the
                  react-snap HTML carries it for crawlers) and the browser handles
                  toggling, keyboard and screen-reader state without JS (A03). */}
              {faqs.map((faq) => (
                <details
                  key={faq.question}
                  className="group border border-gray-200 rounded-lg overflow-hidden bg-white"
                >
                  <summary className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors flex items-center justify-between gap-3 cursor-pointer list-none [&::-webkit-details-marker]:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600">
                    <h2 className="flex items-center gap-3 text-base font-semibold text-gray-900">
                      <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0" aria-hidden="true" />
                      <span>{faq.question}</span>
                    </h2>
                    <ChevronDown
                      className="w-5 h-5 text-gray-500 flex-shrink-0 transition-transform group-open:rotate-180"
                      aria-hidden="true"
                    />
                  </summary>
                  <div className="px-6 pb-4 pt-2 bg-gray-50">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>

          {/* Additional Help Section */}
          <div className="mt-16 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Still Have Questions?
              </h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Free SERP Checker is built and maintained by one person, Daniel Pajuelo, an SEO
                consultant. If something doesn't work or the FAQ doesn't answer your question,
                you can write to him from his website.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="https://danielpajuelo.com/sobre-mi/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <span>Contact Daniel</span>
                </a>
                <a 
                  href="/"
                  className="btn-secondary inline-flex items-center space-x-2"
                >
                  <span>Try the Tool Now</span>
                </a>
              </div>
            </div>
          </div>

          {/* Related SEO Tools Section */}
          <div className="mt-16 bg-gray-50 rounded-2xl p-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Related SEO Tools
              </h3>
              <p className="text-gray-600">
                Explore these powerful SEO tools from Daniel Pajuelo to enhance your SEO strategy
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  name: "Hreflang Checker",
                  url: "https://danielpajuelo.com/comprobador-de-hreflang/",
                  description: "Analyze and validate hreflang tags"
                },
                {
                  name: "Hreflang Generator",
                  url: "https://danielpajuelo.com/generador-online-de-etiquetas-hreflang/",
                  description: "Generate hreflang tags automatically"
                },
                {
                  name: ".htaccess Generator",
                  url: "https://danielpajuelo.com/htaccess-redirecciones-generador/",
                  description: "Create .htaccess redirects easily"
                },
                {
                  name: "Headings Checker",
                  url: "https://danielpajuelo.com/comprobador-de-cabeceras-headings-h1-h6/",
                  description: "Check and analyze heading structure"
                }
              ].map((tool, index) => (
                <a 
                  key={index} 
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-200 group"
                >
                  <div className="text-center">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {tool.name}
                    </h4>
                    <p className="text-xs text-gray-600 mb-3">
                      {tool.description}
                    </p>
                    <div className="inline-flex items-center space-x-1 text-blue-600 text-xs font-medium">
                      <span>Visit Tool</span>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FAQPage;
